import React, { useEffect, useState, useMemo } from "react";
import { Card } from "react-bootstrap";
import { Bar } from "react-chartjs-2";
import YearSelector from "../../components/YearSelector";
import { api } from "../../helpers/apiCore";
import 'chartjs-adapter-moment';
import {
  Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend
} from 'chart.js/auto';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

interface Metric {
  month: string;
  count: number;
}

interface MetricChartProps {
  title: string;
  endpoint: string;
  datasetMap: Record<string, string>;
  queryParamKey: string;
}

export default function MetricChart({
  title,
  endpoint,
  datasetMap,
  queryParamKey
}: MetricChartProps) {
  const datasetKeys = Object.keys(datasetMap);

  const [metrics, setMetrics] = useState<Metric[]>([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  const [selectedDataset, setSelectedDataset] = useState(datasetKeys[0]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchMetrics = async () => {
      setLoading(true);
      try {
        api.setAuthorization(api.getLoggedInUser());
        const params = new URLSearchParams({
          [queryParamKey]: datasetMap[selectedDataset],
          year: selectedYear
        });
        const response = await api.get(endpoint, { params });
        setMetrics(response.data || []);
      } catch (error) {
        console.error(`Error fetching ${title}:`, error);
      } finally {
        setLoading(false);
      }
    };
    fetchMetrics();
  }, [endpoint, datasetMap, selectedDataset, selectedYear, queryParamKey, title]);

  const chartData = useMemo(() => {
    const labels = metrics.map(m => m.month);
    const data = metrics.map(m => m.count);
    return {
      labels,
      datasets: [{
        label: selectedDataset.charAt(0).toUpperCase() + selectedDataset.slice(1),
        data,
        backgroundColor: '#116DFF',
        barThickness: 10
      }]
    };
  }, [metrics, selectedDataset]);

  return (
    <Card style={{ width: '1200px' }}>
      <Card.Body>
        <div className="metrics-card-top">
          <h4>{title}</h4>
          <YearSelector onChange={setSelectedYear} />
        </div>

        <div className="dataset-selector">
          {datasetKeys.map(key => (
            <span
              key={key}
              className={selectedDataset === key ? 'active-dataset' : ''}
              onClick={() => setSelectedDataset(key)}
            >
              {key.charAt(0).toUpperCase() + key.slice(1)}
            </span>
          ))}
        </div>

        {loading ? (
          <div>Loading...</div>
        ) : (
          <Bar
            data={chartData}
            options={{
              plugins: { legend: { display: true } },
              scales: {
                x: {
                  type: 'category',
                  grid: { color: () => '#FFF' }
                },
                y: {
                  beginAtZero: true,
                  border: { dash: [5, 5] },
                  grid: { color: () => '#D6E6FE' }
                }
              }
            }}
          />
        )}
      </Card.Body>
    </Card>
  );
}
